import { ToastrService } from 'ngx-toastr';
import { Component, OnInit } from '@angular/core';
import { MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { ProgramMaintenanceService } from '../program-maintenance.service';
import { UntypedFormGroup, UntypedFormBuilder, ValidatorFn, AbstractControl, Validators } from '@angular/forms';
import { tap, startWith, map, finalize } from 'rxjs/operators';
import { TprecmApiService } from 'src/modules/api';
import { Engineer } from 'src/modules/api/tprecm-api.interface';
import { Observable } from 'rxjs';

interface UnassociatedMescProgram {
   id: string;
   programCode: string;
   programName: string;
   modelYear: number;
   style: string;
   active: boolean;
}

@Component({
   selector: 'add-program-modal',
   templateUrl: './add-program-modal.component.html',
   styleUrls: ['./add-program-modal.component.scss']
})
export class AddProgramModalComponent implements OnInit {

   addProgramForm: UntypedFormGroup;
   loading = false;
   unassociatedMescPrograms: any[];
   engineers: Engineer[];
   egms: Engineer[];
   filteredMescPrograms$: Observable<UnassociatedMescProgram[]>;
   filteredEngineers$: Observable<Engineer[]>;
   filteredEgm$: Observable<Engineer[]>;

   constructor(
      private readonly fb: UntypedFormBuilder,
      private readonly dialogRef: MatDialogRef<AddProgramModalComponent>,
      private readonly programService: ProgramMaintenanceService,
      private readonly tprecmService: TprecmApiService,
      private readonly toastr: ToastrService
   ) {
      this.addProgramForm = this.fb.group({
         mescProgram: ['',
            (control: AbstractControl): { [key: string]: any } | null =>
               control.value.id ? null : { 'noMescId': { value: control.value } }
         ],
         manager2: ['',
            (control: AbstractControl): { [key: string]: any } | null =>
               (control.value === '' || control.value.id) ? null : { 'noManager2Id': { value: control.value } }
         ],
         engineer2: ['',
            (control: AbstractControl): { [key: string]: any } | null =>
               (control.value === '' || control.value.id) ? null : { 'noEngineer2Id': { value: control.value } }
         ],
         newProgram: [''],
         ecm: [true],
         sop: [''],
         fe31: [''],
         fe32: [''],
         fe33: [''],
         bodyShopTest: [''],
         hemPass2: [''],
         endOfProduction: [''],
         active: [true],
         noLitigationHold: [false],
      }, { validator: this.activeAndHasSop() });
   }

   activeAndHasSop(): ValidatorFn {

      return (form: UntypedFormGroup): { [key: string]: any } | null => {
         const active = form.get('active');
         const sop = form.get('sop');
         const valid = !active.value || sop.value;
         sop.setErrors(valid ? null : { 'required': true });

         return valid ? null : { 'mustHaveSop': { value: sop.value } };
      }
   }

   ngOnInit() {
      this.programService.getUnAssociatedMescPrograms().pipe(
         tap(res => {
            if (res) {
               this.unassociatedMescPrograms = res;
            }
         }),
         tap(() => {
            this.filteredMescPrograms$ = this.addProgramForm.controls.mescProgram.valueChanges
               .pipe(
                  startWith(''),
                  map(value => {
                     const filterVal = (typeof value === 'string') ? value.toLowerCase() : '';
                     return this.unassociatedMescPrograms
                        .filter(p => !!p.programName)
                        .filter(p => p.programName.toLowerCase().indexOf(filterVal) !== -1) as UnassociatedMescProgram[];
                  })
               );
         })
      ).subscribe();

      this.tprecmService.getEngineers().pipe(
         tap(res => {
            if (res) { this.engineers = res; }
         }),
         tap(() => {
            this.filteredEngineers$ = this.addProgramForm.controls.engineer2.valueChanges
               .pipe(
                  startWith(''),
                  map(value => {
                     const filterVal = (typeof value === 'string') ? value.toLowerCase() : '';
                     return this.engineers
                        .filter(e => !!e.lastFirstName)
                        .filter(e => e.lastFirstName.toLowerCase().indexOf(filterVal) !== -1);
                  })
               );
         })
      ).subscribe();

      this.tprecmService.getProgramMSE()
         .pipe(
            tap(res => {
               if (res) { this.egms = res; }
            }),
            tap(() => {
               this.filteredEgm$ = this.addProgramForm.controls.manager2.valueChanges
                  .pipe(
                     startWith(''),
                     map(value => {
                        const filterVal = (typeof value === 'string') ? value.toLowerCase() : '';
                        return this.egms
                           .filter(e => !!e.lastFirstName)
                           .filter(e => e.lastFirstName.toLowerCase().indexOf(filterVal) !== -1);
                     })
                  );
            })
         ).subscribe();

   }

   displayMescPrograms(mescProgram: UnassociatedMescProgram) {
      return mescProgram && mescProgram.programName ? mescProgram.programName : '';
   }

   displayEngineers(engineer: Engineer) {
      return engineer && engineer.lastFirstName ? engineer.lastFirstName : '';
   }

   onCloseClick() {
      this.dialogRef.close();
   }

   onAddClick() {
      this.loading = true;
      const body = {};
      const form = this.addProgramForm.value;
      for (const prop in form) {
         if (prop === 'mescProgram') {
            body['mescId'] = form[prop].id;
         } else if (prop === 'engineer2') {
            body['engineer2Id'] = form[prop].id;
         } else if (prop === 'manager2') {
            body['manager2Id'] = form[prop].id;
         } else {
            body[prop] = form[prop];
         }
      }


      this.programService.associateMescProgram(body)
         .pipe(finalize(() => this.loading = false))
         .subscribe(
            () => {
               this.toastr.success('MESC Program associated in TPRECM successfully!');
               this.dialogRef.close({ reload: true });
            }
         );
   }

}
